import React, { memo, ReactNode, useCallback, useMemo } from 'react'

import { FieldInputProps, useField } from 'formik'
import { useDeepCompareMemo } from 'use-deep-compare'

import { Form, FieldSettings, RadioOption, FormHelpersProviderReturn } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { Radio } from '../../utils/shared/Radio'
import { RadioFormControl } from '../../utils/shared/RadioFormControl'
import { RadioFormControlLabel } from '../../utils/shared/RadioFormControlLabel'
import { RadioFormLabel } from '../../utils/shared/RadioFormLabel'
import { RadioGroup as RadioGroupComponentBase } from '../../utils/shared/RadioGroup'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

export type RadioGroupProps = FieldInputProps<Partial<Form>> & {
  className?: string
  required?: boolean
  label?: ReactNode
  helperText?: ReactNode
  helperData?: object
  name: string
  options: RadioOption[]
  error?: string
}

const RadioGroupBase = memo(
  ({
    label,
    className,
    required = false,
    name,
    options,
    error,
    helperText,
    helperData,
    onChange,
    onBlur,
    value,
  }: RadioGroupProps) => {
    return (
      <RadioFormControl className={className} error={Boolean(error)} fullWidth>
        <RadioFormLabel error={Boolean(error)} required={required}>
          <Translate>{label}</Translate>
        </RadioFormLabel>

        <RadioGroupComponentBase
          aria-label={label?.toString()}
          onChange={onChange}
          onBlur={onBlur}
          value={value?.toString() || ''}
        >
          {options.map((option) => (
            <RadioFormControlLabel
              id={`${option.value}-label`}
              key={option.value}
              value={option.value}
              name={name}
              control={
                <Radio
                  inputProps={{
                    'aria-labelledby': `${option.value}-label`,
                  }}
                />
              }
              label={<Translate>{option.label}</Translate>}
            />
          ))}
        </RadioGroupComponentBase>

        <FieldHelperText helperData={helperData} helperText={helperText} name={name} error={error} />
      </RadioFormControl>
    )
  }
)

RadioGroupBase.displayName = 'RadioGroupBase'

export type RadioGroupContainerProps = Omit<RadioGroupProps, 'onChange' | 'onBlur' | 'value'> & {
  value?: string
}

export const RadioGroup = (props: RadioGroupContainerProps) => {
  const submitCount = useSubmitCount()

  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = getHasError(meta, submitCount ?? 1)

  const memoizedField = useDeepCompareMemo(() => field, [field])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.value, formHelpers as FormHelpersProviderReturn, e)
      memoizedField.onChange(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <RadioGroupBase error={hasError ? meta.error : undefined} {...props} {...memoizedField} onChange={handleChange} />
  )
}
